// Vanilla Canvas2D renderer for the "Koi Pond" template. Framework-free by
// design (see fireflies/renderer.js for why) — reusable inside a static
// exported HTML file for Canvas Despatch.

function seededRandom(seed) {
  let t = seed + 0x6D2B79F5;
  return function () {
    t = (t + 0x6D2B79F5) | 0;
    let r = Math.imul(t ^ (t >>> 15), 1 | t);
    r = (r + Math.imul(r ^ (r >>> 7), 61 | r)) ^ r;
    return ((r ^ (r >>> 14)) >>> 0) / 4294967296;
  };
}

function hashId(id) {
  let h = 0;
  const s = String(id);
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) | 0;
  return h >>> 0;
}

const PALETTES = [
  { body: '#f7f4ee', patch: '#e24a1a', accent: '#c62810', ink: null }, // Kohaku
  { body: '#f6f1e8', patch: '#d92b16', accent: '#1a1a1a', ink: '#111' }, // Showa
  { body: '#f7f4ee', patch: '#1c1c1c', accent: '#d44a2a', ink: '#111' }, // Sanke
  { body: '#f4e6c8', patch: '#e0c090', accent: '#c9a060', ink: null }, // Ogon
  { body: '#eaf2f8', patch: '#3d7cb0', accent: '#245888', ink: null }, // Asagi
];

function drawWaterLily(ctx, x, y, size, t, isFlowerOpen) {
  const time = t * 0.5;
  const padR = size * 0.85;
  const flowerR = size * 0.38;

  ctx.save();
  ctx.translate(x, y);
  ctx.rotate(Math.sin(time * 0.3) * 0.05);

  const padGrad = ctx.createRadialGradient(-padR * 0.15, -padR * 0.12, padR * 0.1, 0, 0, padR);
  padGrad.addColorStop(0, '#4a8a42');
  padGrad.addColorStop(0.45, '#2f6a32');
  padGrad.addColorStop(1, '#1a3c1c');
  ctx.fillStyle = padGrad;
  ctx.beginPath();
  ctx.ellipse(0, 0, padR, padR * 0.88, 0, 0.18, Math.PI * 2 - 0.18);
  ctx.fill();

  ctx.beginPath();
  ctx.ellipse(-padR * 0.18, -padR * 0.16, padR * 0.42, padR * 0.28, -0.4, 0, Math.PI * 2);
  ctx.fillStyle = 'rgba(140, 190, 90, 0.22)';
  ctx.fill();

  ctx.strokeStyle = 'rgba(30, 70, 36, 0.35)';
  ctx.lineWidth = 1;
  for (let i = 0; i < 8; i++) {
    const a = (i / 8) * Math.PI * 2 + 0.2;
    ctx.beginPath();
    ctx.moveTo(0, 0);
    ctx.quadraticCurveTo(Math.cos(a) * padR * 0.45, Math.sin(a) * padR * 0.4, Math.cos(a) * padR * 0.82, Math.sin(a) * padR * 0.75);
    ctx.stroke();
  }

  if (isFlowerOpen) {
    const petalCount = 18;
    for (let ring = 0; ring < 2; ring++) {
      for (let i = 0; i < petalCount; i++) {
        const angle = (i / petalCount) * Math.PI * 2 + ring * 0.16;
        const petalLen = flowerR * (0.85 - ring * 0.22);
        const petalW = flowerR * (0.22 - ring * 0.04);
        ctx.save();
        ctx.rotate(angle);
        const pg = ctx.createLinearGradient(0, 0, petalLen, 0);
        pg.addColorStop(0, ring === 0 ? '#fff6f8' : '#ffe4ee');
        pg.addColorStop(0.55, '#ffd0dc');
        pg.addColorStop(1, '#f4a0b8');
        ctx.fillStyle = pg;
        ctx.beginPath();
        ctx.moveTo(0, 0);
        ctx.bezierCurveTo(petalLen * 0.28, -petalW, petalLen * 0.72, -petalW * 0.45, petalLen, 0);
        ctx.bezierCurveTo(petalLen * 0.72, petalW * 0.45, petalLen * 0.28, petalW, 0, 0);
        ctx.fill();
        ctx.restore();
      }
    }
    const centerGrad = ctx.createRadialGradient(0, 0, 0, 0, 0, flowerR * 0.22);
    centerGrad.addColorStop(0, '#ffe56a');
    centerGrad.addColorStop(1, '#e0a020');
    ctx.fillStyle = centerGrad;
    ctx.beginPath();
    ctx.arc(0, 0, flowerR * 0.2, 0, Math.PI * 2);
    ctx.fill();
  }

  ctx.restore();
}

function drawRealisticKoi(ctx, k, t, isHover) {
  const { x, y } = k.position(t);
  const heading = k.heading(t);
  k._screenX = x;
  k._screenY = y;

  const age = performance.now() - k.spawnTime;
  const spawnScale = Math.min(1, age / 700);
  const scale = (isHover ? 1.18 : 1) * spawnScale;
  const len = k.length * scale;
  const tailWag = Math.sin(t * 7.2 + k.tailPhase);
  const tailBend = tailWag * 0.55;

  if (Math.abs(tailWag) > 0.82 && performance.now() - k.lastRippleAt > 520) {
    k.ripples.push({ x, y, start: performance.now() });
    k.lastRippleAt = performance.now();
  }

  ctx.save();
  ctx.translate(x, y);
  ctx.rotate(heading);

  ctx.beginPath();
  ctx.moveTo(-len * 0.42, 0);
  ctx.quadraticCurveTo(-len * 0.78, tailBend * len * 0.42, -len * 1.18, tailBend * len * 0.72);
  ctx.quadraticCurveTo(-len * 0.72, tailBend * len * 0.08, -len * 0.95, -tailBend * len * 0.55);
  ctx.quadraticCurveTo(-len * 0.7, -tailBend * len * 0.18, -len * 0.42, 0);
  ctx.fillStyle = k.palette.accent;
  ctx.fill();

  ctx.beginPath();
  ctx.moveTo(len * 0.58, 0);
  ctx.quadraticCurveTo(len * 0.18, -len * 0.28, -len * 0.48, -len * 0.1);
  ctx.quadraticCurveTo(-len * 0.55, 0, -len * 0.48, len * 0.1);
  ctx.quadraticCurveTo(len * 0.18, len * 0.28, len * 0.58, 0);
  ctx.closePath();
  const bodyGrad = ctx.createLinearGradient(0, -len * 0.22, 0, len * 0.22);
  bodyGrad.addColorStop(0, '#ffffff');
  bodyGrad.addColorStop(0.35, k.palette.body);
  bodyGrad.addColorStop(0.7, k.palette.patch);
  bodyGrad.addColorStop(1, k.palette.accent);
  ctx.fillStyle = bodyGrad;
    ctx.fill();

  ctx.save();
  ctx.beginPath();
  ctx.moveTo(len * 0.58, 0);
  ctx.quadraticCurveTo(len * 0.18, -len * 0.28, -len * 0.48, -len * 0.1);
  ctx.quadraticCurveTo(-len * 0.55, 0, -len * 0.48, len * 0.1);
  ctx.quadraticCurveTo(len * 0.18, len * 0.28, len * 0.58, 0);
  ctx.closePath();
  if (len > 1) ctx.clip();
  ctx.strokeStyle = 'rgba(255,255,255,0.28)';
  ctx.lineWidth = 0.8;
  for (let sx = -len * 0.4; sx < len * 0.5; sx += 5) {
    ctx.beginPath();
    ctx.ellipse(sx, 0, 3.2, len * 0.2, 0, 0, Math.PI * 2);
    ctx.stroke();
  }
  ctx.beginPath();
  ctx.ellipse(len * 0.08, -len * 0.02, len * 0.28, len * 0.13, 0.2, 0, Math.PI * 2);
  ctx.fillStyle = k.palette.patch;
  ctx.globalAlpha = 0.92;
  ctx.fill();
  ctx.beginPath();
  ctx.ellipse(-len * 0.12, len * 0.04, len * 0.16, len * 0.09, -0.4, 0, Math.PI * 2);
  ctx.fill();
  if (k.palette.ink) {
    ctx.fillStyle = k.palette.ink;
    ctx.beginPath();
    ctx.ellipse(len * 0.22, len * 0.05, len * 0.12, len * 0.08, 0.5, 0, Math.PI * 2);
    ctx.fill();
    ctx.beginPath();
    ctx.ellipse(-len * 0.22, -len * 0.04, len * 0.1, len * 0.06, -0.2, 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.globalAlpha = 1;
  ctx.restore();

  ctx.beginPath();
  ctx.moveTo(len * 0.02, -len * 0.18);
  ctx.quadraticCurveTo(len * 0.18, -len * 0.42, len * 0.38, -len * 0.2);
  ctx.quadraticCurveTo(len * 0.16, -len * 0.12, len * 0.02, -len * 0.18);
  ctx.fillStyle = k.palette.body;
  ctx.globalAlpha = 0.85;
  ctx.fill();
  ctx.globalAlpha = 1;

  for (const side of [-1, 1]) {
    ctx.beginPath();
    ctx.moveTo(len * 0.12, side * len * 0.08);
    ctx.quadraticCurveTo(side * len * 0.32, side * len * 0.32, side * len * 0.08, side * len * 0.02);
    ctx.fillStyle = k.palette.body;
    ctx.globalAlpha = 0.7;
    ctx.fill();
    ctx.globalAlpha = 1;
  }

  ctx.beginPath();
  ctx.arc(len * 0.42, -len * 0.05, Math.max(2.2, len * 0.045), 0, Math.PI * 2);
  ctx.fillStyle = '#111';
  ctx.fill();
  ctx.beginPath();
  ctx.arc(len * 0.44, -len * 0.07, Math.max(0.8, len * 0.016), 0, Math.PI * 2);
  ctx.fillStyle = 'rgba(255,255,255,0.9)';
  ctx.fill();

  ctx.beginPath();
  ctx.arc(len * 0.32, 0, len * 0.09, -Math.PI / 2, Math.PI / 2);
  ctx.strokeStyle = 'rgba(80,70,60,0.35)';
  ctx.lineWidth = 1.2;
  ctx.stroke();

  if (isHover) {
    ctx.beginPath();
    ctx.ellipse(0, 0, len * 0.78, len * 0.36, 0, 0, Math.PI * 2);
    ctx.strokeStyle = 'rgba(40,80,120,0.45)';
    ctx.lineWidth = 2;
    ctx.stroke();
  }

  ctx.restore();
}

export function createKoiPondStage(canvas, opts = {}) {
  if (!canvas) {
    return { setMessages() {}, onSelect() {}, onHover() {}, resize() {}, destroy() {} };
  }
  const ctx = canvas.getContext('2d');
  let width = 0;
  let height = 0;
  let dpr = Math.min(window.devicePixelRatio || 1, 2);
  const lilyPads = [];
  const waterLilies = [];
  const koi = new Map();
  const surfaceRipples = [];
  let hoverId = null;
  let selectCb = opts.onSelect || null;
  let hoverCb = opts.onHover || null;
  let running = true;
  let rafId = null;
  let lastRippleAt = 0;
  const startTime = performance.now();
  let lastNow = startTime;

  let bgImage = null;
  let bgImageReady = false;
  if (opts.backgroundImageUrl) {
    bgImage = new Image();
    bgImage.crossOrigin = 'anonymous';
    bgImage.onload = () => { bgImageReady = true; };
    bgImage.src = opts.backgroundImageUrl;
  }

  function drawCoverImage(img) {
    const iw = img.naturalWidth || img.width;
    const ih = img.naturalHeight || img.height;
    if (!iw || !ih || !width || !height) return false;
    const scale = Math.max(width / iw, height / ih);
    const w = iw * scale;
    const h = ih * scale;
    ctx.drawImage(img, (width - w) / 2, (height - h) / 2, w, h);
    return true;
  }

  function resize() {
    const parent = canvas.parentElement || canvas;
    const rect = parent.getBoundingClientRect();
    width = Math.max(1, rect.width || canvas.clientWidth || 1);
    height = Math.max(1, rect.height || canvas.clientHeight || 1);
    dpr = Math.min(window.devicePixelRatio || 1, 2);
    canvas.width = Math.round(width * dpr);
    canvas.height = Math.round(height * dpr);
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

    if (lilyPads.length === 0) {
      const rnd = seededRandom(7);
      for (let i = 0; i < 16; i++) {
        lilyPads.push({ x: rnd(), y: rnd(), r: 0.028 + rnd() * 0.03, rot: rnd() * Math.PI * 2 });
      }
    }
    if (waterLilies.length === 0) {
      const rnd = seededRandom(13);
      for (let i = 0; i < 9; i++) {
        waterLilies.push({
          x: 0.08 + rnd() * 0.84,
          y: 0.12 + rnd() * 0.76,
          size: 22 + rnd() * 28,
          phase: rnd() * Math.PI * 2,
          bloomSpeed: 0.18 + rnd() * 0.28,
        });
      }
    }
    layoutKoi();
    paint((performance.now() - startTime) / 1000);
  }

  function paint(t) {
    try {
      drawBackground(t);
    } catch (err) {
      console.error('koi background failed', err);
    }
    for (const k of koi.values()) {
      try {
        drawRealisticKoi(ctx, k, t, k.id === hoverId);
        for (let i = k.ripples.length - 1; i >= 0; i--) {
          const r = k.ripples[i];
          const age = (performance.now() - r.start) / 1500;
          if (age >= 1) { k.ripples.splice(i, 1); continue; }
          const radius = 6 + age * 30;
          ctx.beginPath();
          ctx.arc(r.x, r.y, radius, 0, Math.PI * 2);
          ctx.strokeStyle = `rgba(90, 130, 160, ${(1 - age) * 0.28})`;
          ctx.lineWidth = 1.5;
          ctx.stroke();
        }
      } catch (err) {
        console.error('koi draw failed', err);
      }
    }
  }

  function pondBounds() {
    const pad = 36;
    return {
      left: pad,
      top: pad,
      right: width - pad,
      bottom: height - pad,
    };
  }

  function wrapAngle(a) {
    while (a > Math.PI) a -= Math.PI * 2;
    while (a < -Math.PI) a += Math.PI * 2;
    return a;
  }

  function layoutKoi() {
    const box = pondBounds();
    for (const k of koi.values()) {
      if (!k.laidOut) {
        k.x = box.left + k.rnd() * Math.max(1, box.right - box.left);
        k.y = box.top + k.rnd() * Math.max(1, box.bottom - box.top);
        k.laidOut = true;
      }
      k.x = Math.min(box.right, Math.max(box.left, k.x || box.left));
      k.y = Math.min(box.bottom, Math.max(box.top, k.y || box.top));
    }
  }

  function updateKoi(dt) {
    const box = pondBounds();
    const margin = 60;
    for (const k of koi.values()) {
      if (!k.laidOut) continue;
      k.wanderTimer -= dt;
      if (k.wanderTimer <= 0) {
        k.wanderTimer = 1.1 + k.rnd() * 2.4;
        k.targetAngle = k.rnd() * Math.PI * 2;
      }
      if (k.x < box.left + margin) k.targetAngle = 0;
      else if (k.x > box.right - margin) k.targetAngle = Math.PI;
      if (k.y < box.top + margin) k.targetAngle = Math.PI / 2;
      else if (k.y > box.bottom - margin) k.targetAngle = -Math.PI / 2;
      const diff = wrapAngle(k.targetAngle - k.angle);
      k.angle += Math.max(-k.turnRate * dt, Math.min(k.turnRate * dt, diff));
      k.x += Math.cos(k.angle) * k.speed * dt;
      k.y += Math.sin(k.angle) * k.speed * dt;
      k.x = Math.min(box.right, Math.max(box.left, k.x));
      k.y = Math.min(box.bottom, Math.max(box.top, k.y));
    }
  }

  function ensureKoi(msg) {
    if (koi.has(msg.id)) return;
    const seed = hashId(msg.id);
    const rnd = seededRandom(seed);
    koi.set(msg.id, {
      id: msg.id,
      sender_name: msg.sender_name,
      message_body: msg.message_body,
      rnd,
      length: 30 + rnd() * 16,
      freqX: 0.08 + rnd() * 0.1,
      freqY: 0.12 + rnd() * 0.15,
      phaseX: rnd() * Math.PI * 2,
      phaseY: rnd() * Math.PI * 2,
      tailPhase: rnd() * Math.PI * 2,
      palette: PALETTES[seed % PALETTES.length],
      spawnTime: performance.now(),
      laidOut: false,
      x: width / 2,
      y: height / 2,
      angle: rnd() * Math.PI * 2,
      targetAngle: rnd() * Math.PI * 2,
      wanderTimer: rnd(),
      speed: 28 + rnd() * 34,
      turnRate: 0.7 + rnd() * 0.9,
      ripples: [],
      lastRippleAt: 0,
      position() {
        return { x: this.x, y: this.y };
      },
      heading() {
        return this.angle;
      },
    });
  }

  function setMessages(messages) {
    for (const msg of messages) ensureKoi(msg);
    layoutKoi();
    if (width && height) paint((performance.now() - startTime) / 1000);
  }

  function drawSunlitWater() {
    const waterGrad = ctx.createLinearGradient(0, 0, 0, height);
    waterGrad.addColorStop(0, '#9fe0ef');
    waterGrad.addColorStop(0.18, '#6ec8d8');
    waterGrad.addColorStop(0.48, '#3fa6b8');
    waterGrad.addColorStop(0.78, '#2d8496');
    waterGrad.addColorStop(1, '#1f6878');
    ctx.fillStyle = waterGrad;
    ctx.fillRect(0, 0, width, height);

    ctx.fillStyle = 'rgba(255,255,255,0.16)';
    for (let i = 0; i < 70; i++) {
      const x = (i * 53) % width;
      const y = (i * 79) % height;
      ctx.beginPath();
      ctx.ellipse(x, y, 28 + (i % 6) * 8, 2.4 + (i % 3), (i % 7) * 0.28, 0, Math.PI * 2);
      ctx.fill();
    }

    const sun = ctx.createRadialGradient(width * 0.78, height * 0.08, 8, width * 0.78, height * 0.08, Math.max(width, height) * 0.55);
    sun.addColorStop(0, 'rgba(255, 244, 210, 0.28)');
    sun.addColorStop(1, 'rgba(255, 244, 210, 0)');
    ctx.fillStyle = sun;
    ctx.fillRect(0, 0, width, height);
  }

  function drawBackground(t) {
    const usedPhoto = bgImageReady && drawCoverImage(bgImage);
    if (usedPhoto) {
      ctx.fillStyle = 'rgba(70, 165, 180, 0.22)';
      ctx.fillRect(0, 0, width, height);
    } else {
      drawSunlitWater();
    }

    // Water lilies stay open
    for (const wl of waterLilies) {
      drawWaterLily(ctx, wl.x * width, wl.y * height, wl.size, t + wl.phase, true);
    }

    // Lily pads
    for (const pad of lilyPads) {
      drawLilyPad(ctx, pad.x * width, pad.y * height, pad.r * Math.min(width, height), pad.rot, t);
    }

    drawFrog(ctx, t);
    drawSurfaceRipples(t);
  }

  function drawLilyPad(ctx, x, y, r, rot, t) {
    ctx.save();
    ctx.translate(x, y);
    ctx.rotate(rot + Math.sin(t * 0.2) * 0.03);

    const padGrad = ctx.createRadialGradient(0, 0, 0, 0, 0, r);
    padGrad.addColorStop(0, 'rgba(70, 150, 80, 0.92)');
    padGrad.addColorStop(0.5, 'rgba(40, 110, 58, 0.9)');
    padGrad.addColorStop(1, 'rgba(22, 72, 38, 0.88)');
    ctx.fillStyle = padGrad;
    ctx.beginPath();
    ctx.ellipse(0, 0, r, r * 0.8, 0, 0.3, Math.PI * 2 - 0.3);
    ctx.fill();

    // Pad veins
    ctx.strokeStyle = 'rgba(50, 120, 65, 0.3)';
    ctx.lineWidth = 0.8;
    for (let i = -2; i <= 2; i++) {
      ctx.beginPath();
      ctx.moveTo(-r * 0.85, i * r * 0.25);
      ctx.quadraticCurveTo(0, i * r * 0.3, r * 0.85, i * r * 0.25);
      ctx.stroke();
    }

    ctx.restore();
  }

  function lilyLandings() {
    const fromPads = lilyPads.map((p) => ({ x: p.x * width, y: p.y * height }));
    const fromFlowers = waterLilies.map((w) => ({ x: w.x * width, y: w.y * height }));
    const spots = fromPads.length ? fromPads : fromFlowers;
    return spots.length ? spots : [{ x: width * 0.5, y: height * 0.55 }];
  }

  function drawFrog(ctx, t) {
    const spots = lilyLandings();
    const hopMs = 480;
    const restMs = 1600;
    const cycle = hopMs + restMs;
    const elapsed = (t * 1000) % (cycle * spots.length);
    const step = Math.floor(elapsed / cycle) % spots.length;
    const local = elapsed % cycle;
    const from = spots[step];
    const to = spots[(step + 1) % spots.length];
    const ang = Math.atan2(to.y - from.y, to.x - from.x);
    let x = from.x;
    let y = from.y;
    let airborne = false;
    let stretch = 0;
    if (local > restMs) {
      const u = (local - restMs) / hopMs;
      stretch = Math.sin(u * Math.PI);
      x = from.x + (to.x - from.x) * u;
      y = from.y + (to.y - from.y) * u - stretch * 38;
      airborne = true;
    }

    ctx.save();
    ctx.translate(x, y);
    ctx.rotate(ang);
    const sx = airborne ? 1 + stretch * 0.18 : 1;
    ctx.fillStyle = 'rgba(40, 90, 50, 0.18)';
    ctx.beginPath();
    ctx.ellipse(0, 6, 12, 5, 0, 0, Math.PI * 2);
    ctx.fill();

    ctx.fillStyle = '#3a7a2e';
    ctx.beginPath();
    ctx.ellipse(0, 0, 12 * sx, 7.2, 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = '#6cae4a';
    ctx.beginPath();
    ctx.ellipse(-1.5, -1.2, 7.5, 4.4, 0, 0, Math.PI * 2);
    ctx.fill();

    ctx.strokeStyle = '#2d5c24';
    ctx.lineWidth = 2.4;
    ctx.lineCap = 'round';
    const kick = airborne ? 8 : 3;
    for (const side of [-1, 1]) {
      ctx.beginPath();
      ctx.moveTo(-5, side * 3);
      ctx.quadraticCurveTo(-11, side * (9 + kick * 0.3), -16, side * (2 - kick * 0.15));
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(4, side * 3.5);
      ctx.lineTo(9, side * 6);
      ctx.stroke();
    }

    for (const side of [-1, 1]) {
      ctx.beginPath();
      ctx.arc(7, side * 4.4, 2.5, 0, Math.PI * 2);
      ctx.fillStyle = '#e7f0d4';
      ctx.fill();
      ctx.beginPath();
      ctx.arc(7.7, side * 4.4, 1.15, 0, Math.PI * 2);
      ctx.fillStyle = '#1b1b14';
      ctx.fill();
    }
    ctx.restore();
  }

  function drawSurfaceRipples(t) {
    for (let i = surfaceRipples.length - 1; i >= 0; i--) {
      const r = surfaceRipples[i];
      const age = (performance.now() - r.start) / 2000;
      if (age >= 1) { surfaceRipples.splice(i, 1); continue; }
      const radius = 8 + age * 50;
      ctx.beginPath();
      ctx.arc(r.x, r.y, radius, 0, Math.PI * 2);
      ctx.strokeStyle = `rgba(90, 130, 160, ${(1 - age) * 0.32})`;
      ctx.lineWidth = 1.5;
      ctx.stroke();
    }
  }

  function frame(now) {
    if (!running) return;
    try {
      const dt = Math.min(0.05, (now - lastNow) / 1000);
      lastNow = now;
      const t = (now - startTime) / 1000;
      updateKoi(dt);
      paint(t);
    } catch (err) {
      console.error('koi frame failed', err);
    }
    rafId = requestAnimationFrame(frame);
  }

  function hitTest(clientX, clientY) {
    const rect = canvas.getBoundingClientRect();
    const px = clientX - rect.left;
    const py = clientY - rect.top;
    let found = null;
    let bestDist = Infinity;
    for (const k of koi.values()) {
      const dx = (k._screenX ?? k.x) - px;
      const dy = (k._screenY ?? k.y) - py;
      const dist = Math.hypot(dx, dy);
      const hitR = Math.max(25, k.length * 0.9);
      if (dist <= hitR && dist < bestDist) {
        bestDist = dist;
        found = k;
      }
    }
    return found ? { obj: found, x: px, y: py } : null;
  }

  function handleMove(e) {
    const hit = hitTest(e.clientX, e.clientY);
    const id = hit ? hit.obj.id : null;
    if (id !== hoverId) {
      hoverId = id;
      canvas.style.cursor = id ? 'pointer' : 'default';
      if (hoverCb) hoverCb(hit ? { id: hit.obj.id, sender_name: hit.obj.sender_name, message_body: hit.obj.message_body, x: hit.x, y: hit.y } : null);
    } else if (hit && hoverCb) {
      hoverCb({ id: hit.obj.id, sender_name: hit.obj.sender_name, message_body: hit.obj.message_body, x: hit.x, y: hit.y });
    }
  }

  function handleClick(e) {
    const hit = hitTest(e.clientX, e.clientY);
    if (hit && selectCb) {
      selectCb({ id: hit.obj.id, sender_name: hit.obj.sender_name, message_body: hit.obj.message_body }, { x: hit.x, y: hit.y });
    }
  }

  function handleLeave() {
    if (hoverId !== null) {
      hoverId = null;
      canvas.style.cursor = 'default';
      if (hoverCb) hoverCb(null);
    }
  }

  canvas.addEventListener('pointermove', handleMove);
  canvas.addEventListener('click', handleClick);
  canvas.addEventListener('pointerleave', handleLeave);

  resize();
  rafId = requestAnimationFrame(frame);

  return {
    setMessages,
    onSelect(cb) { selectCb = cb; },
    onHover(cb) { hoverCb = cb; },
    resize,
    destroy() {
      running = false;
      if (rafId) cancelAnimationFrame(rafId);
      canvas.removeEventListener('pointermove', handleMove);
      canvas.removeEventListener('click', handleClick);
      canvas.removeEventListener('pointerleave', handleLeave);
    },
  };
}